const SKIP_FIELD_KEYS = new Set(['non_field_errors', 'detail', 'message']);

/** Prefer these DRF field keys when several are present (stable UX for auth errors). */
const DRF_FIELD_PRIORITY = [
  'code',
  'otp_token',
  'ephemeral_token',
  'enrollment_token',
  'method',
  'username',
  'password',
  'email',
];

function orderedFieldEntries(body: Record<string, unknown>): [string, unknown][] {
  const keys = Object.keys(body).filter(key => !SKIP_FIELD_KEYS.has(key));
  const preferred = DRF_FIELD_PRIORITY.filter(key => keys.includes(key));
  const rest = keys
    .filter(key => !DRF_FIELD_PRIORITY.includes(key))
    .sort((a, b) => a.localeCompare(b));
  const orderedKeys = [...preferred, ...rest];
  return orderedKeys.map(key => [key, body[key]] as [string, unknown]);
}

function firstStringInList(value: unknown): string | null {
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    return value[0];
  }
  return null;
}

export function extractDrfResponseMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const body = data as Record<string, unknown>;

  const nonField = body.non_field_errors;
  if (Array.isArray(nonField) && nonField.length > 0 && typeof nonField[0] === 'string') {
    return nonField[0];
  }

  const detail = body.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  const fromDetailList = firstStringInList(detail);
  if (fromDetailList) {
    return fromDetailList;
  }

  if (typeof body.message === 'string') {
    return body.message;
  }

  for (const [, value] of orderedFieldEntries(body)) {
    if (typeof value === 'string') {
      return value;
    }
    const fromList = firstStringInList(value);
    if (fromList) {
      return fromList;
    }
  }

  return null;
}

function messageFromDetailsProperty(error: object): string | null {
  if (!('details' in error)) {
    return null;
  }
  const raw = (error as { details?: unknown }).details;
  if (raw == null) {
    return null;
  }
  return extractDrfResponseMessage(raw);
}

function hasAxiosResponse(error: unknown): error is {
  response: { data?: Record<string, string[] | string | undefined> };
} {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: unknown }).response != null &&
    typeof (error as { response: unknown }).response === 'object'
  );
}

function extractErrorField(
  error: unknown,
  fieldPath?: string,
  defaultMessage: string = 'An error occurred'
): string | null {
  if (!error) {
    return fieldPath ? null : defaultMessage;
  }

  // fetchWrapper ApiError has no axios `response`; prefer `details` before generic `message`
  if (
    !fieldPath &&
    typeof error === 'object' &&
    error !== null &&
    !hasAxiosResponse(error)
  ) {
    const fromStandaloneDetails = messageFromDetailsProperty(error);
    if (fromStandaloneDetails) {
      return fromStandaloneDetails;
    }
  }

  if (hasAxiosResponse(error)) {
    const { data } = error.response;

    if (data) {
      if (fieldPath) {
        const fieldValue = data[fieldPath];
        if (Array.isArray(fieldValue) && fieldValue[0]) {
          return fieldValue[0];
        }
        if (typeof fieldValue === 'string') {
          return fieldValue;
        }
        const altFieldPath = `${fieldPath}_id`;
        const altFieldValue = data[altFieldPath];
        if (Array.isArray(altFieldValue) && altFieldValue[0]) {
          return altFieldValue[0];
        }
        if (typeof altFieldValue === 'string') {
          return altFieldValue;
        }
        return null;
      }

      const drfMessage = extractDrfResponseMessage(data);
      if (drfMessage) {
        return drfMessage;
      }
    }
  }

  if (typeof error === 'object' && error !== null) {
    const fromMergedDetails = messageFromDetailsProperty(error);
    if (fromMergedDetails) {
      return fromMergedDetails;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return fieldPath ? null : defaultMessage;
}

export const extractApiErrorMessage = (
  error: unknown,
  defaultMessage: string = 'An error occurred'
): string => {
  return extractErrorField(error, undefined, defaultMessage) ?? defaultMessage;
};

export const extractEstablishmentError = (error: unknown): string | null => {
  return extractErrorField(error, 'establishment');
};

